import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { Components } from '@fasstech/flexiget';
import { always, compose, concat, cond, equals, filter, isEmpty, map, prop, propOr, T, when, isNotNil } from 'ramda';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Loading from '../../../components/Loading';
import Trees from '../../../components/treeStructure/Trees';
import { Collapse } from '../../../components';
import OperationStatusSection from '../../../components/OperationStatusSection';
import { FinishedRemoveWidgetsSubscriptionQuery } from '../../../_graphql/subscriptions/removeWidgets/FinishedRemoveWidgetsSubscription';
import ProgressModalSubscription from '../../../components/ProgressModalSubscription';
import useOfferRole from '../../../hooks/useOfferRole';
import useActionGuard from '../../../hooks/useActionGuard';
import { canEditOfferItem } from '../../../components/users/utils/roles';
import { removeUnusedWidgets } from './hooks/useRemoveUnusedWidgets';
import WidgetTreeRow from './WidgetTreeRow';

const WidgetsSection = (props) => {
  const { args, widgets, setWidgets, setRefetchWidgets, reFetch, reFetchFolders, folders, offerIsDraft, setFullscreenLoading, collapseIsOpen, setCollapseIsOpen, isLoading, isPublished, isLoadingFolders, user, userTeam } = props;

  const [treeProps, setTreeProps] = useState({
    addTree: () => {},
    expandedKeys: []
  });
  const [isOpen, setIsOpen] = useState(false);
  const [errorRemoveWidgets, setErrorRemoveWidgets] = useState(false);
  const { loading, run } = useActionGuard();

  useEffect(() => {
    const value = sessionStorage.getItem('collapseWidgetIsOpen');
    setCollapseIsOpen(!!JSON.parse(value));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => sessionStorage.setItem('collapseWidgetIsOpen', collapseIsOpen), [collapseIsOpen]);

  const { offerId, scope } = args;
  const history = useHistory();
  const handleCreateNewFolder = () => treeProps.addTree(null, treeProps.expandedKeys)();

  const { userIsAdmin, userIsOfferEditor } = useOfferRole(offerId);
  const userCanEditWidgetsTree = canEditOfferItem({
    userIsAdmin, userIsOfferEditor, scope, isDraft: offerIsDraft
  });

  const goToWidget = () => compose(
    when(isNotNil, history.push),
    cond([
      [equals('offer'), always(`/offre/${offerId}/edition/widgets/creer`)],
      [T, always(null)]
    ])
  )(scope);

  const widgetsIdUnused = compose(map(prop('id')), filter((widget) => !prop('isUsed', widget)))(widgets);

  const handleRemoveUnusedWidgets = () => run(`remove-unused-widgets-${offerId}`, (done) => removeUnusedWidgets({
    widgets,
    folders,
    setIsOpen,
    setErrorRemoveWidgets,
    done
  }));

  const isLoadingWidgets = isLoading || isLoadingFolders;

  const handleModalLoadingClick = () => {
    reFetchFolders();
    setRefetchWidgets(true);
    reFetch();
  };

  const extractStatus = response => response.finishedRemoveWidgetsSubscription;

  const initialStatusRemoveWidgets = {
    step: 0,
    widgets: false
  };
  return (
    <>
      <ProgressModalSubscription
        isOpen={isOpen}
        subscription={FinishedRemoveWidgetsSubscriptionQuery}
        variables={{ filter: { userId: prop('userId', user), itemIds: [`removeWidgets-${prop('teamId', userTeam)}`] } }}
        infoLoading={(status, progress, error) => (
          <OperationStatusSection
            operationType="la suppression des widgets inutilisés"
            items={[
              { label: 'Widgets inutilisés supprimés', type: 'widgets' }
            ]}
            status={status}
            progress={progress}
            errorMsg="Impossible de supprimer correctement les éléments de l'offre, veuillez contacter l'équipe Flexidocs"
            showError={error}
          />
        )}
        onFinish={handleModalLoadingClick}
        extractStatus={extractStatus}
        initialStatus={initialStatusRemoveWidgets}
        setIsOpen={setIsOpen}
        error={errorRemoveWidgets}
      />
      <Collapse
        className="section-widget"
        collapseIsOpen={collapseIsOpen}
        setCollapseIsOpen={setCollapseIsOpen}
        title={equals('global', scope) ? 'Widgets de l\'équipe' : 'Widgets de l\'offre'}
        actions={userCanEditWidgetsTree && (
          <>
            {collapseIsOpen && (
              <Button
                onClick={handleCreateNewFolder}
                icon={<PlusOutlined />}
              >
                Créer un dossier racine
              </Button>
            )}
            <Button
              onClick={goToWidget}
              icon={<PlusOutlined />}
            >
              Créer un widget
            </Button>
            <Components.Tooltip
              title="Supprimer les widgets inutilisés"
            >
              <Button
                disabled={isEmpty(widgetsIdUnused)}
                onClick={handleRemoveUnusedWidgets}
                loading={propOr(false, `remove-unused-widgets-${offerId}`, loading)}
                icon={<DeleteOutlined className="btn-remove-unused-widgets"/>}
              />
            </Components.Tooltip>
          </>
        )}
      >
        {isLoadingWidgets ? <Loading /> : (
          <Trees
            isPublished={isPublished}
            elements={concat(widgets, folders)} setElements={setWidgets} ElementRow={<WidgetTreeRow {...props} originalWidgets={widgets} setIsLoading={setFullscreenLoading} /> }
            setTreeProps={setTreeProps} setRefetchWidgets={setRefetchWidgets} userIsAdmin={userCanEditWidgetsTree}
          />
        )}
      </Collapse>
    </>
  );
};

export default WidgetsSection;
